import React from 'react';
import {
  Image,
  ImageStyle,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { colors } from '../../theme';
import Text from './Text';

/**
 * Somebody else, as a circle.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT `ProfileAvatar`
 * ---------------------------------------------------------------------------
 * `components/account/ProfileAvatar` draws the person who is signed in, and
 * its last resort is the Homingo petal mark - which is right there, because
 * "we know nothing about you yet" is a reasonable thing to say to the account
 * holder about themselves.
 *
 * It is the wrong thing to say about a professional or a driver. A brand mark
 * where a person's face should be reads as "Homingo is calling", which is
 * exactly the confusion an in-app call has to avoid: the customer needs to
 * know they are talking to a named human, not to the company. So this one
 * falls back to initials and, failing that, to a plain tinted circle - never
 * to the logo.
 *
 * Two letters rather than one, for the same reason: on a call screen the
 * circle is 96pt across and carries the whole identity of the person ringing.
 * "R" is a placeholder; "RK" beside "Ramesh K." is a portrait.
 */

export type AvatarTone = 'brand' | 'muted' | 'inverse';

type Props = {
  name?: string | null;
  /** A remote or local URI. Cropped to fill, never letterboxed. */
  photo?: string | null;
  /** Diameter in points. */
  size?: number;
  /**
   * `brand` - forest fill, white letters. The default, for a card on canvas.
   * `muted` - pale sage fill, forest letters. For a row that already has a
   *   filled control on it and does not need a second solid shape.
   * `inverse` - for the call overlay, which is drawn on the dark surface and
   *   would otherwise put a forest circle on a forest ground.
   */
  tone?: AvatarTone;
  /* Typed for both branches: the photo is an `Image` and the fallback a
     `View`, and `ViewStyle` alone is not assignable to `ImageStyle` - the two
     disagree about `overflow: 'scroll'`, which an Image cannot do. */
  style?: StyleProp<ViewStyle & ImageStyle>;
};

const fills: Record<AvatarTone, string> = {
  brand: colors.primary,
  muted: colors.surfaceMuted,
  inverse: colors.surfaceMuted,
};

const inks: Record<AvatarTone, string> = {
  brand: colors.onPrimary,
  muted: colors.primary,
  inverse: colors.primary,
};

/**
 * Up to two initials, uppercased.
 *
 * Trimmed and split on any run of whitespace, so "  ramesh   k. " letters as
 * "RK" rather than as a space and a full stop. Returns null when there is no
 * letter in the string at all, which draws the empty circle rather than a
 * box with nothing in it.
 */
export const initialsOf = (name?: string | null): string | null => {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const letters = parts
    .slice(0, 2)
    .map(part => part.charAt(0))
    .join('')
    .replace(/[^\p{L}\p{N}]/gu, '');

  return letters ? letters.toUpperCase() : null;
};

function Avatar({ name, photo, size = 48, tone = 'brand', style }: Props) {
  const initials = initialsOf(name);
  const round = { width: size, height: size, borderRadius: size / 2 };

  if (photo) {
    return (
      <Image
        source={{ uri: photo }}
        style={[styles.photo, round, style]}
        resizeMode="cover"
        accessible
        accessibilityRole="image"
        accessibilityLabel={name ? `${name}'s photo` : 'Photo'}
      />
    );
  }

  return (
    <View
      style={[styles.circle, round, { backgroundColor: fills[tone] }, style]}
      accessible
      accessibilityRole="image"
      accessibilityLabel={name ?? 'Unnamed'}
    >
      {initials ? (
        /* Sized off the circle rather than off a type variant, so one
           component covers the 96pt call overlay and the 40pt driver row
           without a second style - the same trick `ProfileAvatar` uses. */
        <Text
          color={inks[tone]}
          style={[
            styles.initials,
            { fontSize: size * 0.36, lineHeight: size * 0.44 },
          ]}
        >
          {initials}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  photo: {
    backgroundColor: colors.surfaceAccent,
  },
  circle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initials: {
    fontWeight: '700',
    /* Otherwise Android centres the glyphs against the font's full metrics and
       the letters sit a couple of points high in the circle. */
    textAlign: 'center',
    includeFontPadding: false,
  },
});

export default Avatar;
